import { QueryFunctionContext, queryOptions } from "@tanstack/react-query";
import type { QueryParams } from "services/restUtil";
import { QueryError } from "services/errors";
import { BaseApiQueryOptions } from "./utils";

// Full query keys are `[key, params, payload]`. For a path-like key, keep the declared
// route pattern rather than the interpolated URL, so every variant of
// `/skills/category/:categoryId` shares one `queryKeyPrefix`.
export type TalaeraQueryKey<
  TQueryParams extends QueryKeyParams = void,
  TPayload = void,
> = readonly [key: string, queryParams: TQueryParams, payload: TPayload];

export type QueryKeyParams = QueryParams | void | undefined;

// For a plain GET, `key` is also the request path.
type ApiQueryConfig<TResponse, TParams extends QueryKeyParams, TPayload, TData> = {
  key: string;
  queryFn?: QueryFn<TResponse, TParams, TPayload>;
} & BaseApiQueryOptions<TResponse, TParams, TPayload, TData>;

type QueryFn<TResponse, TParams extends QueryKeyParams, TPayload = void> = (args: {
  key: string;
  params: TParams;
  payload: TPayload;
  context: QueryFunctionContext<TalaeraQueryKey<TParams, TPayload>>;
}) => Promise<TResponse>;

/**
 * Creates a query-options factory with Talaera's standard cache key shape.
 *
 * `queryFn` can be omitted for plain GET requests; the default is
 * `api.get(key, params)`. Custom `queryFn`s receive `key`, `params`, and `payload`.
 */
export function apiQuery<
  TResponse,
  TParams extends QueryKeyParams = void,
  TPayload = void,
  TData = TResponse,
>(config: ApiQueryConfig<TResponse, TParams, TPayload, TData>) {
  const defaultQueryFn: QueryFn<TResponse, TParams, TPayload> = async args => {
    // Not a module-scope import: `restUtil` reaches `users/auth` and from there most
    // of the app, which would loop back through every service that calls `apiQuery`
    // while this module is still evaluating.
    const { api } = await import("services/restUtil");
    // Cache-key params are intentionally looser than wire params (custom queryFns may
    // carry rich shapes); queries relying on this default GET must use scalar params.
    return api.get<TResponse>(args.key, (args.params ?? null) as QueryParams | null);
  };

  const { key, queryFn = defaultQueryFn, ...restConfig } = config;

  const wrappedQueryFn = (
    context: QueryFunctionContext<TalaeraQueryKey<TParams, TPayload>>
  ) => {
    const [, params, payload] = context.queryKey;
    return queryFn({ key, params, payload, context }).catch(error => {
      const queryError =
        error instanceof QueryError
          ? error
          : new QueryError(error, context.meta?.errorMessages);
      throw queryError;
    });
  };

  function createQueryOptions(params: TParams, payload: TPayload) {
    return queryOptions({
      ...restConfig,
      queryKey: [key, params, payload] as const,
      queryFn: wrappedQueryFn,
    });
  }

  createQueryOptions.queryKeyPrefix = [key];

  return createQueryOptions as typeof createQueryOptions & {
    queryKeyPrefix: [TalaeraQueryKey<TParams, TPayload>[0]];
  };
}

export { infiniteApiQuery } from "./infiniteQueries";
export type { PaginatedResponse } from "./infiniteQueries";
export { QueryError } from "services/errors";
